import type { ChartOptions } from 'chart.js';
import React from 'react';
import ReactDOM, { Root } from 'react-dom/client';
import { ChartTooltip, ChartTooltipColors } from '@components/ChartTooltip/ChartTooltip.component';
import { PlcColor, PlcFonts } from '@styles/colors';
import { Spacing } from '@styles/Spacing.style';
import { formatDateCompact, plcDayjs } from '@utils/date';
import { ConfigType } from 'dayjs';

export type TimeChartData = Array<{ date: Date }>;

const TOOLTIP_ID = 'chartjs-tooltip';
let tooltipRoot: Root | null = null;
let tooltipContainerEl: HTMLElement | null = null;

/* ---------- X bounds helpers ---------- */

export function getTimeChartXMin(data: TimeChartData) {
  return data.length > 0 ? plcDayjs(data[0].date).subtract(1, 'day').toString() : undefined;
}

export function getTimeChartXMax(data: TimeChartData) {
  return data.length > 0
    ? plcDayjs(data[data.length - 1].date)
        .add(1, 'day')
        .toString()
    : undefined;
}

/* ---------- Dataset helpers (compat Chart.js v4) ---------- */

type XYPoint = number | { x?: number | string | Date; y?: number | null | undefined };
type PlcDataset = { data?: XYPoint[] };

function getDatasetYValues(dataset: PlcDataset): number[] {
  const arr = dataset.data ?? [];
  return arr.map((d) => (typeof d === 'number' ? d : (d?.y ?? NaN))).filter((n) => Number.isFinite(n)) as number[];
}

export function getDatasetsMinOrMax(datasets: PlcDataset[], method: 'min' | 'max') {
  return datasets.reduce<number | undefined>((previous, dataset) => {
    const yValues = getDatasetYValues(dataset);
    const values = [previous, ...yValues].filter((v) => typeof v === 'number' && Number.isFinite(v)) as number[];
    return values.length ? Math[method](...values) : previous;
  }, undefined);
}

export function getTimeChartBounds(data: TimeChartData, datasets: PlcDataset[]) {
  const xMin = getTimeChartXMin(data);
  const xMax = getTimeChartXMax(data);
  const minValue = getDatasetsMinOrMax(datasets, 'min');
  const maxValue = getDatasetsMinOrMax(datasets, 'max');
  const yMin = maxValue !== undefined && minValue !== undefined ? minValue - (maxValue - minValue) * 0.1 : undefined;
  const yMax = maxValue !== undefined && minValue !== undefined ? maxValue + (maxValue - minValue) * 0.1 : undefined;
  return {
    xMax,
    xMin,
    yMax,
    yMin,
  };
}

/* ---------- Tooltip DOM host ---------- */

function getTooltipEl(opacity: number) {
  // Tooltip Element
  let tooltipEl = document.getElementById(TOOLTIP_ID);

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = TOOLTIP_ID;
    document.body.appendChild(tooltipEl);
  }
  tooltipEl.style.opacity = opacity.toString();
  tooltipEl.classList.add('plc-tooltip');
  tooltipEl.classList.remove('above', 'below', 'no-transform');
  tooltipEl.style.display = opacity === 0 ? 'none' : '';
  return tooltipEl;
}

/* ---------- External tooltip (Chart.js v4) ---------- */

export function buildTooltipV4(
  ctx: any,
  unit: string,
  colors?: ChartTooltipColors,
  titleCallback: (d: ConfigType) => string = formatDateCompact
) {
  const { chart, tooltip } = ctx || {};
  const datasets = chart?.data?.datasets as any[] | undefined;

  const tooltipEl = getTooltipEl(tooltip?.opacity ?? 0);

  // Cacher si pas de tooltip/datasets ou pas de datapoints
  if (!tooltip || !datasets || !tooltip.dataPoints?.length || tooltip.opacity === 0) {
    tooltipEl.style.display = 'none';
    return;
  }

  if (!tooltipRoot || tooltipContainerEl !== tooltipEl) {
    if (tooltipRoot) {
      tooltipRoot.unmount();
    }
    tooltipRoot = ReactDOM.createRoot(tooltipEl);
    tooltipContainerEl = tooltipEl;
  }

  // Données
  const first = tooltip.dataPoints[0];
  const dataIndex: number = first.dataIndex;

  const rawX = first.parsed?.x ?? tooltip.title?.[0];
  const title = titleCallback(rawX as ConfigType);

  const yCandidates = tooltip.dataPoints
    .map((dp: any) => dp.element?.y)
    .filter((y: unknown) => typeof y === 'number') as number[];

  const caretY = Number.isFinite(tooltip.caretY) ? (tooltip.caretY as number) : Infinity;
  const y = yCandidates.length ? Math.min(...yCandidates, caretY) : Number.isFinite(caretY) ? caretY : 0;

  // Render React tooltip
  const reactEl = React.createElement(ChartTooltip, {
    colors,
    datasets,
    index: dataIndex,
    title,
    unit,
  });
  tooltipRoot.render(reactEl);

  // Position
  const rect = chart.canvas.getBoundingClientRect();
  const { width: tooltipWidth } = tooltipEl.getBoundingClientRect();

  let left = Math.max(tooltipWidth / 2, rect.left + window.pageXOffset + (tooltip.caretX ?? 0));
  left = Math.min(left, window.innerWidth - tooltipWidth / 2);
  const top = rect.top + window.pageYOffset + (Number.isFinite(y) ? y : 0);

  Object.assign(tooltipEl.style, {
    display: 'block',
    left: `${left}px`,
    pointerEvents: 'none',
    position: 'absolute',
    top: `${top}px`,
    zIndex: '2147483647',
  });
}

/* ---------- Options v4 ---------- */
export function getTimeChartOptions(
  xMax: string | undefined,
  xMin: string | undefined,
  yMax: number | undefined,
  yMin: number | undefined,
  tooltipUnit = '',
  tooltipColors?: ChartTooltipColors,
  tooltipTitleCallback?: (d: ConfigType) => string,
  timeUnit?: 'day' | 'week' | 'month' | 'year',
  maxXTicks?: number
): ChartOptions<'line'> {
  const axesLineColor = '#979797';

  return {
    animation: false,
    elements: {
      point: { radius: 0 },
    },

    interaction: {
      axis: 'x',
      intersect: false,
      mode: 'nearest',
    },

    maintainAspectRatio: false,

    plugins: {
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: (ctx) => buildTooltipV4(ctx, tooltipUnit, tooltipColors, tooltipTitleCallback),
      },
    },

    responsive: true,
    scales: {
      x: {
        border: {
          color: axesLineColor,
        },
        grid: {
          color: axesLineColor,
          drawOnChartArea: false,
          drawTicks: false,
        },
        max: xMax,
        min: xMin,
        ticks: {
          color: PlcColor.GREY_CLOUD_DARK,
          font: { family: PlcFonts.Montserrat as unknown as string },
          maxTicksLimit: maxXTicks ?? (timeUnit === 'month' ? 14 : timeUnit === 'week' ? 10 : 12),
          padding: 24,
        },
        time: {
          displayFormats: {
            day: 'D MMM',
            month: 'MMM YY',
            week: 'D MMM',
            year: 'YYYY',
          },
          round: timeUnit,
          unit: timeUnit,
        },
        type: 'time',
      },
      y: {
        border: {
          color: axesLineColor,
          dash: [1, 5],
        },
        grid: {
          color: axesLineColor,
          drawTicks: false,
          tickLength: 0,
        },
        max: yMax,
        min: yMin,
        ticks: {
          callback: (value, index, ticks) => {
            if (yMax && index === 0) {
              return '';
            }
            if (yMin && index === (ticks as any[]).length - 1 && value !== 0) {
              return '';
            }
            return `${value}`;
          },
          color: PlcColor.GREY_CLOUD_DARK,
          font: { family: PlcFonts.Montserrat as unknown as string },
          padding: Spacing.s,
        },
      },
    },
  };
}

export function removeTooltip() {
  if (tooltipRoot) {
    tooltipRoot.unmount();
    tooltipRoot = null;
    tooltipContainerEl = null;
  }
  const el = document.getElementById(TOOLTIP_ID);
  if (el) {
    el.remove();
  }
}
